/**
 * DeleteBoardColumnModal Component
 * Confirmation modal for deleting board columns
 * Handles column deletion with confirmation
 */
import React, { useState } from 'react';
import { 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  Button, 
  Box, 
  Typography, 
  Avatar
} from '@mui/material';
import { Warning } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { COLORS } from '../../shared/colors';

const DeleteBoardColumnModal = ({ open, onClose, onConfirm, columnData }) => {
  const { t } = useTranslation();
  const [isDeleting, setIsDeleting] = useState(false);

  const handleClose = (...args) => {
    if (isDeleting) return;
    onClose && onClose(...args);
  };

  const handleDelete = async () => {
    if (isDeleting) return;

    setIsDeleting(true);
    try {
      await Promise.resolve(onConfirm && onConfirm(columnData));
      onClose && onClose();
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose} 
      disableEscapeKeyDown={isDeleting} 
      maxWidth="xs" 
      fullWidth
      PaperProps={{ className: 'pm-modal-paper' }}
    >
      <DialogTitle sx={{ pt: 4, pb: 1, position: 'relative' }}>
        <Box display="flex" flexDirection="column" alignItems="center" gap={1.5}>
          <Avatar sx={{ bgcolor: 'rgba(211, 47, 47, 0.08)', color: '#d32f2f', width: 56, height: 56 }}>
            <Warning sx={{ fontSize: '2rem' }} />
          </Avatar>
          <Typography variant="h6" fontWeight="700" color="text.primary" align="center" sx={{ mt: 1 }}>
            {t('boardColumn.deleteColumn')}
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ px: 3, py: 2, textAlign: 'center' }}>
        <Typography sx={{ mb: 1.5, color: 'text.primary', fontSize: '0.95rem' }}>
          {t('boardColumn.confirmDeleteColumn')}
        </Typography>
        <Box sx={{ bgcolor: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: '6px', py: 1.5, px: 2, mb: 2 }}>
          <Typography variant="subtitle1" fontWeight="700" color="#d32f2f" sx={{ wordBreak: 'break-word', whiteSpace: 'normal' }}>
            {columnData?.title}
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem' }}>
          {t('boardColumn.tasksNeedToBeMoved')}
        </Typography>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 4, pt: 1, display: 'flex', gap: 2 }}>
        <Button 
          onClick={handleClose} 
          variant="outlined" 
          disabled={isDeleting}
          sx={{ flex: 1, color: 'text.primary', borderColor: COLORS.gray[300], py: '0.5rem', textTransform: 'none', fontWeight: 600 }}
        >
          {t('common.cancel')}
        </Button>
        <Button 
          onClick={handleDelete} 
          variant="contained" 
          color="error"
          disabled={isDeleting} 
          sx={{ flex: 1, bgcolor: '#d32f2f', color: COLORS.white, py: '0.5rem', textTransform: 'none', fontWeight: 600, '&:hover': { bgcolor: '#b71c1c' } }}
        >
          {isDeleting ? t('common.deleting', 'Deleting...') : t('common.delete')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DeleteBoardColumnModal;
